"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fns_1 = require("../helpers/fns");
class GTMBrowserDriver {
    constructor(config) {
        this.name = 'GTMBrowserDriver';
        this.layerId = 'dataLayer';
        if (config && config.layerId) {
            this.layerId = config.layerId;
        }
    }
    load() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            window[this.layerId] = window[this.layerId] || [];
            return Object.prototype.hasOwnProperty.call(window, this.layerId);
        });
    }
    supportsEvent(event) {
        return GTMBrowserDriver.SUPPORTED_EVENTS.includes(event.name) || event.name.indexOf('custom.') >= 0;
    }
    track(event) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if ((0, fns_1.isCustomEvent)(event)) {
                return yield this.trackCustom(event);
            }
            const data = event.getData();
            switch (event.name) {
                case 'page_view':
                    if (data.turnOffPageViewForSPA === 1) {
                        return;
                    }
                    return yield this.trackPageView(data);
                case 'add_payment_info':
                    return yield this.trackAddPaymentInfo(data);
                case 'add_shipping_info':
                    return yield this.trackAddShippingInfo(data);
                case 'add_to_cart':
                    return yield this.trackAddToCart(data);
                case 'begin_checkout':
                    return yield this.trackBeginCheckout(data);
                case 'purchase':
                    return yield this.trackPurchase(data);
                case 'pre_purchase':
                    return yield this.trackPrePurchase(data);
                case 'typ_purchase':
                    return yield this.trackTypPurchase(data);
                case 'remove_from_cart':
                    return yield this.trackRemoveFromCart(data);
                case 'view_cart':
                    return yield this.trackViewCart(data);
                case 'view_item':
                    return yield this.trackViewItem(data);
                case 'view_item_list':
                    return yield this.trackViewItemList(data);
                case 'login':
                    return yield this.trackLogin(data);
                case 'sign_up':
                    return yield this.trackSignUp(data);
                default:
                    throw new TypeError(`Event ${event.name} not supported!`);
            }
        });
    }
    trackCustom(event) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const data = event.forDriver(this.name);
            if (data === null) {
                return;
            }
            const ev = {
                type: '',
                name: '',
                payload: {}
            };
            const supportedTypes = ['ecommerce', 'common'];
            if (typeof data.event_type !== 'string' || !supportedTypes.includes(data.event_type)) {
                throw new TypeError(`Custom event ${event.name} has to provide event type for ${this.name} [forDriver.event_type]. Supported types: ${supportedTypes.join(', ')}`);
            }
            ev.type = data.event_type;
            if (typeof data.event_name !== 'string') {
                throw new TypeError(`Custom event ${event.name} has to provide event name for ${this.name} [forDriver.event_name]`);
            }
            ev.name = data.event_name;
            if (data.event_payload === null || typeof data.event_payload !== 'object') {
                throw new TypeError(`Custom event ${event.name} has to provide event payload for ${this.name} [forDriver.event_payload]`);
            }
            ev.payload = data.event_payload;
            switch (ev.type) {
                case 'ecommerce':
                    return this.pushEcommerce(ev.name, ev.payload);
                case 'common':
                    return this.pushCommon(ev.name, ev.payload);
                default:
                    throw new TypeError(`Custom event ${event.name} not supported!`);
            }
        });
    }
    trackPageView(data) {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushCommon((_a = data.customEventName) !== null && _a !== void 0 ? _a : 'page_view', Object.assign({ page_path: data.pagePath, page_title: data.pageTitle, language: data.language, currency: data.currency }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackAddPaymentInfo(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('add_payment_info', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), coupon: data.coupon, payment_type: data.paymentMethod, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackAddShippingInfo(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('add_shipping_info', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), coupon: data.coupon, shipping_tier: data.shippingTier, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackAddToCart(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('add_to_cart', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackBeginCheckout(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('begin_checkout', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), coupon: data.coupon, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackPurchase(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('purchase', Object.assign(Object.assign({ currency: data.currency, transaction_id: data.transactionId, value: this.monetaryValue(data.value), affiliation: data.affiliation, coupon: data.coupon, shipping: data.shipping ? this.monetaryValue(data.shipping) : undefined, tax: data.tax ? this.monetaryValue(data.tax) : undefined, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})), (data.isPremium ? { is_premium: data.isPremium } : {})));
            if (data.customer) {
                this.pushCustomer(data.customer);
            }
        });
    }
    trackPrePurchase(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('pre-purchase', Object.assign(Object.assign({ currency: data.currency, transaction_id: data.transactionId, value: this.monetaryValue(data.value), affiliation: data.affiliation, coupon: data.coupon, shipping: data.shipping ? this.monetaryValue(data.shipping) : undefined, tax: data.tax ? this.monetaryValue(data.tax) : undefined, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})), (data.isPremium ? { is_premium: data.isPremium } : {})));
        });
    }
    trackTypPurchase(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('typ-purchase', Object.assign(Object.assign({ currency: data.currency, transaction_id: data.transactionId, value: this.monetaryValue(data.value), affiliation: data.affiliation, coupon: data.coupon, shipping: data.shipping ? this.monetaryValue(data.shipping) : undefined, tax: data.tax ? this.monetaryValue(data.tax) : undefined, items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})), (data.isPremium ? { is_premium: data.isPremium } : {})));
        });
    }
    trackRemoveFromCart(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('remove_from_cart', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackViewCart(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('view_cart', Object.assign({ currency: data.currency, totalQuantity: data.totalQuantity, value: this.monetaryValue(data.value), items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackViewItem(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('view_item', Object.assign({ currency: data.currency, value: this.monetaryValue(data.value), items: this.getItems(data) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    trackViewItemList(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushEcommerce('view_item_list', Object.assign({ item_list_id: data.listId, item_list_name: data.listName, items: this.getItems(data, {
                    item_list_name: data.listName,
                    item_list_id: data.listId
                }) }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
    getItems(data, defaults = {}) {
        let indexAdjuster = 0;
        if (Array.isArray(data.items) &&
            data.items[0] &&
            typeof data.items[0].index !== 'undefined' &&
            data.items[0].index !== null &&
            data.items[0].index === 0) {
            indexAdjuster = 1;
        }
        const items = Array.isArray(data.items) ? data.items.map((item, iteration) => ({
            item_id: item.id,
            item_name: item.name,
            affiliation: item.affiliation,
            coupon: item.coupon,
            currency: item.currency,
            discount: item.discount ? this.monetaryValue(item.discount) : undefined,
            index: typeof item.index === 'undefined' ? iteration + 1 : item.index + indexAdjuster,
            item_brand: item.brand,
            item_category: item.category,
            item_category2: item.category2,
            item_category3: item.category3,
            item_category4: item.category4,
            item_category5: item.category5,
            item_list_id: item.listId,
            item_list_name: item.listName,
            item_variant: item.variant,
            location_id: item.locationId,
            price: item.price ? this.monetaryValue(item.price) : undefined,
            quantity: item.quantity
        })) : [];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return items.map((item) => {
            for (const key in defaults) {
                if (typeof item[key] === 'undefined' || item[key] === null) {
                    item[key] = defaults[key];
                }
            }
            return item;
        });
    }
    pushEcommerce(eventName, payload) {
        window[this.layerId].push({ ecommerce: null });
        window[this.layerId].push({
            event: eventName,
            ecommerce: payload
        });
    }
    pushCommon(eventName, payload) {
        window[this.layerId].push(Object.assign({ event: eventName }, payload));
    }
    pushCustomer(payload) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        window[this.layerId].push({
            'customer': {
                'em': payload.email,
                'fn': payload.firstname,
                'ln': payload.lastname,
                'ph': (_a = payload.phone) !== null && _a !== void 0 ? _a : '',
                // i.e. +48 => 0048)
                'external_id': (_b = payload.id) !== null && _b !== void 0 ? _b : '',
                'ge': (_c = payload.gender) !== null && _c !== void 0 ? _c : '',
                'db': (_d = payload.birthDate) !== null && _d !== void 0 ? _d : '',
                'ct': (_e = payload.city) !== null && _e !== void 0 ? _e : '',
                'st': (_f = payload.province) !== null && _f !== void 0 ? _f : '',
                'zp': (_g = payload.zipCode) !== null && _g !== void 0 ? _g : '',
                'country': (_h = payload.country) !== null && _h !== void 0 ? _h : '',
                'is_premium': (_j = payload.isPremium) !== null && _j !== void 0 ? _j : '' // customer belongs to F&F benefit program
            }
        });
    }
    monetaryValue(value) {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }
    trackLogin(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushCustomer(data);
            this.pushCommon('login', Object.assign(Object.assign({ method: data.method, user_id: data.user_id || '', client_id: data.client_id || data.id || '' }, (data.eventId ? { event_id: data.eventId } : {})), { isPremium: data.isPremium || '' }));
        });
    }
    trackSignUp(data) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            this.pushCustomer(data);
            this.pushCommon('sign_up', Object.assign({ method: data.method, user_id: data.user_id || '', client_id: data.client_id || data.id || '' }, (data.eventId ? { event_id: data.eventId } : {})));
        });
    }
}
exports.default = GTMBrowserDriver;
GTMBrowserDriver.SUPPORTED_EVENTS = [
    'page_view',
    'add_payment_info',
    'add_shipping_info',
    'add_to_cart',
    'begin_checkout',
    'purchase',
    'pre_purchase',
    'typ_purchase',
    'remove_from_cart',
    'view_cart',
    'view_item',
    'view_item_list',
    'login',
    'sign_up'
];
//# sourceMappingURL=gtm.browser-driver.js.map